/**
 * CarSitePublicPage — landing pública React para el módulo Car Builder.
 * Ruta: /car-sites/:slug
 *
 * Consume GET /api/car-sites/{slug}/data y renderiza:
 *  - Nav, Hero (carrusel), Nuevos, Seminuevos, Promociones,
 *    Testimonios, Contacto, Mapa, Footer, botón flotante WhatsApp.
 *  - Modales: detalle de modelo, cotizar, prueba de manejo, info seminuevo,
 *    auto a cuenta, confirmación.
 *
 * Branding y toggles llegan del endpoint. Los formularios envían leads
 * a POST /api/webhook/lead/{company_id}?campaign_id={campaign_id}.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, X, Phone, MapPin, Mail, Globe, Clock,
  Loader2, Sparkles, MessageCircle,
  Facebook, Instagram,
} from 'lucide-react';

const PLACEHOLDER_IMG = 'https://placehold.co/800x500/0f172a/64748b/png?text=Sin+imagen';

// Fuente custom (typography_colors.font_file_url) -- mismo patrón EXACTO que
// dev-builder de verdad (components/dev-builder/DevLandingPublic.js:
// fontFormatFor + CUSTOM_FONT_FAMILY). El backend (_render_html, car_builder.py)
// ya lo aplicaba en el Preview autenticado (plantilla nativa) -- esta página
// es la landing pública REAL para tenants sin custom_html_path y nunca lo
// aplicaba, así que la fuente subida no se veía nunca en producción (bug
// reportado 2026-09-12).
function fontFormatFor(url) {
  const ext = (url || '').toLowerCase().split('.').pop();
  if (ext === 'otf') return 'opentype';
  if (ext === 'woff2') return 'woff2';
  if (ext === 'woff') return 'woff';
  return 'truetype';
}
const CUSTOM_FONT_FAMILY = 'CarBuilderCustomFont';

const formatPrice = (value) => {
  const n = Number(value || 0);
  if (!n) return 'Consultar';
  try {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency', currency: 'MXN', minimumFractionDigits: 0,
    }).format(n);
  } catch {
    return `$${n.toLocaleString('es-MX')}`;
  }
};

const formatKm = (km) => {
  const n = Number(km || 0);
  return `${n.toLocaleString('es-MX')} km`;
};

const newModelImage = (item) => item?.media?.gallery?.[0]?.url || PLACEHOLDER_IMG;
const usedImage = (item) => item?.gallery?.[0]?.url || item?.photos?.[0] || PLACEHOLDER_IMG;
const cleanPhone = (p) => String(p || '').replace(/[^0-9]/g, '');

const COLOR_MAP = {
  'Negro': '#1a1a1a', 'Blanco': '#f5f5f5', 'Gris': '#9ca3af',
  'Plateado': '#c0c0c0', 'Azul': '#2563eb', 'Rojo': '#dc2626',
  'Beige': '#d4b896', 'Verde': '#16a34a', 'Café': '#92400e',
  'Naranja': '#ea580c', 'Amarillo': '#ca8a04', 'Morado': '#7c3aed',
};
const colorSwatch = (name) => COLOR_MAP[String(name || '').trim()] || '#888888';

const BADGE_CLIP = 'polygon(5% 0%, 100% 0%, 95% 100%, 0% 100%)';

const usedBadgeBg = (status, primary) => {
  if (status === 'Apartado') return '#eab308';
  if (status === 'Vendido') return '#71717a';
  return primary || '#10b981';
};

// ── Carrusel reutilizable ─────────────────────────────────────────────
const Carousel = ({ images, autoRotate = false, className = '', testid = 'carousel' }) => {
  const [idx, setIdx] = useState(0);
  const safe = (images || []).filter(Boolean);
  const len = safe.length;

  useEffect(() => {
    if (!autoRotate || len < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % len), 5000);
    return () => clearInterval(t);
  }, [autoRotate, len]);

  if (!len) return null;

  const go = (delta) => setIdx((i) => (i + delta + len) % len);

  return (
    <div className={`overflow-hidden ${className}`} data-testid={testid}>
      {safe.map((src, i) => (
        <img
          key={src + i}
          src={src}
          alt=""
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === idx ? 'opacity-100' : 'opacity-0'}`}
        />
      ))}
      {len > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(-1)}
            className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 z-10"
            data-testid={`${testid}-prev`}
            aria-label="Anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 z-10"
            data-testid={`${testid}-next`}
            aria-label="Siguiente"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1 z-10">
            {safe.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full ${i === idx ? 'bg-white' : 'bg-white/40'}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ── Modal overlay genérico ────────────────────────────────────────────
const Modal = ({ open, onClose, title, children, maxWidth = 'max-w-2xl', testid }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/70 flex items-center justify-center p-3 overflow-y-auto"
      onClick={onClose}
      data-testid={testid || 'modal'}
    >
      <div
        className={`bg-white text-zinc-900 rounded-2xl shadow-2xl w-full ${maxWidth} max-h-[92vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-bold">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-900 p-1"
            data-testid={`${testid || 'modal'}-close`}
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

// ── Form genérico de lead ─────────────────────────────────────────────
const LeadForm = ({
  open, onClose, title, mode, companyId, campaignId, branding,
  nuevos, seminuevos, preselectedCarId = '', preselectedUsedId = '',
  onSent,
}) => {
  const [form, setForm] = useState({
    name: '', phone: '', email: '',
    car_model_id: preselectedCarId,
    used_id: preselectedUsedId,
    comments: '',
  });
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({
        name: '', phone: '', email: '',
        car_model_id: preselectedCarId,
        used_id: preselectedUsedId,
        comments: '',
      });
      setFeedback(null);
    }
  }, [open, preselectedCarId, preselectedUsedId]);

  const enabled = Boolean(campaignId);
  const showNuevoSelect = mode === 'cotizar' || mode === 'test_drive';
  const showUsedSelect = mode === 'info_seminuevo';
  const requirePhoneOrEmail = true;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!enabled) return;
    if (!form.name.trim()) {
      setFeedback({ ok: false, msg: 'El nombre es obligatorio' });
      return;
    }
    if (requirePhoneOrEmail && !form.phone.trim() && !form.email.trim()) {
      setFeedback({ ok: false, msg: 'Comparte un teléfono o email para contactarte' });
      return;
    }
    setSending(true);
    setFeedback(null);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const url = `${base}/api/webhook/lead/${encodeURIComponent(companyId)}?campaign_id=${encodeURIComponent(campaignId)}`;
      const body = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
      };
      let notes = '';
      if (mode === 'cotizar') {
        if (form.car_model_id) body.car_model_id = form.car_model_id;
        notes = `Comentarios: ${form.comments || '-'}`;
      } else if (mode === 'test_drive') {
        if (form.car_model_id) body.car_model_id = form.car_model_id;
        notes = `Prueba de manejo | Comentarios: ${form.comments || '-'}`;
      } else if (mode === 'info_seminuevo') {
        const u = (seminuevos || []).find((x) => x.id === form.used_id);
        const label = u ? `${u.marca || ''} ${u.modelo || ''} ${u.año || ''}`.trim() : 'No especificado';
        notes = `Auto de interés: ${label} | Comentarios: ${form.comments || '-'}`;
      } else if (mode === 'auto_cuenta') {
        notes = `Auto a cuenta | Comentarios: ${form.comments || '-'}`;
      }
      body.notes = notes;

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `HTTP ${res.status}`);
      }
      setFeedback({ ok: true, msg: '¡Gracias! Te contactaremos en breve.' });
      onSent?.();
    } catch (err) {
      setFeedback({ ok: false, msg: 'No se pudo enviar. Inténtalo de nuevo.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={title} testid={`lead-form-${mode}`}>
      {!enabled ? (
        <p className="text-sm text-zinc-500" data-testid={`lead-form-${mode}-disabled`}>
          Formulario no disponible.
        </p>
      ) : feedback?.ok ? (
        <div className="text-center py-6" data-testid={`lead-form-${mode}-success`}>
          <Sparkles className="w-10 h-10 mx-auto mb-2" style={{ color: branding?.primary_color || '#10b981' }} />
          <p className="text-base font-semibold">{feedback.msg}</p>
          <button
            type="button"
            onClick={onClose}
            className="mt-4 px-5 py-2 rounded-lg text-white"
            style={{ backgroundColor: branding?.primary_color || '#10b981' }}
            data-testid={`lead-form-${mode}-close-success`}
          >
            Cerrar
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3" data-testid={`lead-form-${mode}-form`}>
          <div>
            <label className="text-xs text-zinc-600 block mb-1">Nombre *</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
              className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              data-testid={`lead-form-${mode}-name`}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-600 block mb-1">Teléfono</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                data-testid={`lead-form-${mode}-phone`}
              />
            </div>
            <div>
              <label className="text-xs text-zinc-600 block mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                data-testid={`lead-form-${mode}-email`}
              />
            </div>
          </div>
          {showNuevoSelect && (nuevos || []).length > 0 && (
            <div>
              <label className="text-xs text-zinc-600 block mb-1">Modelo de interés</label>
              <select
                value={form.car_model_id}
                onChange={(e) => setForm((s) => ({ ...s, car_model_id: e.target.value }))}
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm bg-white"
                data-testid={`lead-form-${mode}-car-select`}
              >
                <option value="">Seleccionar...</option>
                {nuevos.map((m) => (
                  <option key={m.id} value={m.id}>
                    {`${m.general?.brand || ''} ${m.general?.model || ''} ${m.general?.trim || ''} ${m.general?.year || ''}`.trim()}
                  </option>
                ))}
              </select>
            </div>
          )}
          {showUsedSelect && (seminuevos || []).length > 0 && (
            <div>
              <label className="text-xs text-zinc-600 block mb-1">Seminuevo de interés</label>
              <select
                value={form.used_id}
                onChange={(e) => setForm((s) => ({ ...s, used_id: e.target.value }))}
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm bg-white"
                data-testid={`lead-form-${mode}-used-select`}
              >
                <option value="">Seleccionar...</option>
                {seminuevos.map((u) => (
                  <option key={u.id} value={u.id}>
                    {`${u.marca || ''} ${u.modelo || ''} ${u.año || ''}`.trim()}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs text-zinc-600 block mb-1">Comentarios</label>
            <textarea
              rows={3}
              value={form.comments}
              onChange={(e) => setForm((s) => ({ ...s, comments: e.target.value }))}
              className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              data-testid={`lead-form-${mode}-comments`}
            />
          </div>

          {feedback && !feedback.ok && (
            <p className="text-sm text-red-600" data-testid={`lead-form-${mode}-error`}>{feedback.msg}</p>
          )}

          <button
            type="submit"
            disabled={sending}
            className="w-full text-white font-semibold rounded-lg py-2.5 disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ backgroundColor: branding?.primary_color || '#10b981' }}
            data-testid={`lead-form-${mode}-submit`}
          >
            {sending && <Loader2 className="w-4 h-4 animate-spin" />}
            {sending ? 'Enviando...' : 'Enviar'}
          </button>
        </form>
      )}
    </Modal>
  );
};

// ── Modal detalle de modelo nuevo ─────────────────────────────────────
const ModelDetailModal = ({ item, onClose, onCotizar, onTestDrive, branding }) => {
  const open = Boolean(item);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const gallery = (item?.media?.gallery || []).map((g) => g?.url).filter(Boolean);
  const pdfUrl = item?.media?.pdf_url || item?.media?.tech_sheet_pdf_url;
  const t = item?.technical || {};
  const p = item?.pricing_inventory || {};
  const g = item?.general || {};
  const price = p?.sale_price || p?.list_price;
  const primary = branding?.primary_color || '#10b981';

  const motorItems = [
    t?.engine_type,
    t?.power_hp ? `${t.power_hp} HP` : null,
    t?.torque_nm ? `${t.torque_nm} Nm` : null,
    t?.transmission_type,
    t?.traction,
    t?.fuel_type,
    t?.eff_combined ? `Rend. combinado: ${t.eff_combined}` : null,
    t?.seats ? `${t.seats} plazas` : null,
  ].filter((v) => String(v || '').trim() !== '');

  const equipChecklist = Array.isArray(item?.equipment?.checklist) ? item.equipment.checklist : [];
  const equipItems = equipChecklist
    .map((x) => (typeof x === 'string' ? x : x?.label || x?.name))
    .filter(Boolean)
    .slice(0, 10);

  const groups = [
    { title: 'Motor & Rendimiento', items: motorItems },
    { title: 'Equipamiento', items: equipItems },
  ].filter((gr) => gr.items.length > 0);

  const tags = [g?.body_type, g?.trim].filter(Boolean);
  const title = [g?.brand, g?.model, g?.trim, g?.year].filter(Boolean).join(' ') || 'Detalle';

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/70 flex items-center justify-center p-3 overflow-y-auto"
      onClick={onClose}
      data-testid="model-detail-modal"
    >
      <div
        className="relative bg-white text-zinc-900 w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          <Carousel
            images={gallery.length ? gallery : [PLACEHOLDER_IMG]}
            className="relative w-full h-96 bg-zinc-100"
            testid="model-detail-carousel"
          />
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 z-20 bg-black/60 hover:bg-black/80 text-white rounded-full w-10 h-10 flex items-center justify-center"
            aria-label="Cerrar"
            data-testid="model-detail-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-8 sm:p-10">
          <h3 className="text-2xl font-black uppercase text-gray-900 mb-3 leading-tight">
            {title}
          </h3>

          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {tags.map((tg) => (
                <span key={tg} className="bg-gray-100 px-3 py-1 text-[10px] uppercase font-bold">
                  {tg}
                </span>
              ))}
            </div>
          )}

          <div className="mb-8">
            <p className="text-xs uppercase text-gray-400 font-bold tracking-widest">Desde</p>
            <p className="text-3xl font-black" style={{ color: primary }}>
              {formatPrice(price)}
            </p>
          </div>

          {groups.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {groups.map((gr) => (
                <div key={gr.title}>
                  <p className="text-sm font-black uppercase mb-2 text-gray-900">{gr.title}</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {gr.items.map((line, i) => (
                      <li key={`${gr.title}-${i}`}>{line}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          <div className={pdfUrl
            ? 'grid grid-cols-1 md:grid-cols-3 gap-4'
            : 'grid grid-cols-1 md:grid-cols-2 gap-4'
          }>
            <button
              type="button"
              onClick={() => onCotizar(item.id)}
              className="bg-gray-900 text-white py-4 uppercase font-black tracking-widest hover:bg-black transition-all"
              data-testid="model-detail-cotizar-btn"
            >
              Cotizar
            </button>
            <button
              type="button"
              onClick={() => onTestDrive(item.id)}
              className="border-2 border-gray-900 text-gray-900 py-4 uppercase font-black tracking-widest hover:bg-gray-900 hover:text-white transition-all"
              data-testid="model-detail-test-drive-btn"
            >
              Prueba de Manejo
            </button>
            {pdfUrl && (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="text-white py-4 uppercase font-black tracking-widest hover:brightness-110 transition-all text-center"
                style={{ backgroundColor: primary }}
                data-testid="model-detail-tech-sheet"
              >
                Descargar Ficha
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Componente principal ──────────────────────────────────────────────
export default function CarSitePublicPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [detailItem, setDetailItem] = useState(null);
  const [activeForm, setActiveForm] = useState(null); // {mode, preselectedCarId, preselectedUsedId}
  const [promosOpen, setPromosOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const base = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${base}/api/car-sites/${encodeURIComponent(slug)}/data`);
        if (!res.ok) {
          if (!cancelled) setNotFound(true);
          return;
        }
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [slug]);

  // El favicon NO puede resolverse declarativamente como el <title>: index.html
  // ya trae 3 <link rel="icon"> ESTÁTICOS con `sizes` explícito, fuera del
  // árbol de React -- React 19 solo hoistea/gestiona lo que ÉL renderiza, así
  // que un <link> dictado aquí se AGREGA a esos 3, no los reemplaza, y el
  // navegador prefiere los que traen `sizes`. Mismo fix que DevSitePublicPage.js:
  // quitar los 3 estáticos mientras esta pantalla esté montada y devolverlos
  // al desmontar.
  useEffect(() => {
    const faviconUrl = data?.site?.favicon_url;
    if (!faviconUrl) return undefined;

    const staticIcons = Array.from(document.querySelectorAll('link[rel~="icon"]'));
    staticIcons.forEach((el) => el.remove());

    const ownLink = document.createElement('link');
    ownLink.setAttribute('rel', 'icon');
    ownLink.setAttribute('href', faviconUrl);
    document.head.appendChild(ownLink);

    return () => {
      ownLink.remove();
      staticIcons.forEach((el) => document.head.appendChild(el));
    };
  }, [data?.site?.favicon_url]);

  const branding = data?.branding || {};
  const site = data?.site || {};
  const toggles = site.landing_toggles || {};
  const campaigns = site.form_campaigns || {};
  const dealer = site.dealer_info || {};
  const nuevos = data?.nuevos?.items || [];
  const seminuevos = data?.seminuevos?.items || [];
  const promotions = site.promotions || {};
  const promoItems = Array.isArray(promotions.items) ? promotions.items : [];
  // El editor solo conoce las 5 secciones de la plantilla nativa (modelos/
  // seminuevos/refacciones/mi_auto/promociones); esta landing tiene su propio
  // set (nuevos/seminuevos/promociones/testimonios/contacto). Se alían las
  // que sí tienen equivalente real -- refacciones/mi_auto no existen aquí.
  // Consolidado en typography_colors (pedido explícito de Alexis 2026-09-11,
  // replica dev-builder de verdad) -- reemplaza accent_color/
  // section_background_colors sueltos. El backend ya migra en memoria lo que
  // un doc viejo tenía guardado suelto (_resolve_typography_colors).
  const typographyColors = site.typography_colors || {};
  const sectionBg = typographyColors.section_background_colors || {};
  const nuevosBg = sectionBg.modelos || undefined;
  const seminuevosBg = sectionBg.seminuevos || undefined;
  const promoBg = sectionBg.promociones || undefined;

  // Color de TEXTO por sección (mismas 3 llaves que sectionBg tienen
  // equivalente real en esta landing -- ver comentario de arriba sobre
  // refacciones/mi_auto). Aplica al título de cada sección y, en
  // Promociones, también a la descripción (el único "subtítulo" real).
  const sectionText = typographyColors.section_text_colors || {};
  const nuevosTextColor = sectionText.modelos || undefined;
  const seminuevosTextColor = sectionText.seminuevos || undefined;
  const promoTextColor = sectionText.promociones || undefined;

  // Fuente personalizada (@font-face) -- mismo patrón que dev-builder real
  // (DevLandingPublic.js). Se aplica en el wrapper raíz vía fontFamily
  // (hereda a toda la página; ningún elemento de aquí fija su propia
  // font-family, a diferencia de color que sí se pisa por Tailwind en
  // varios lugares -- por eso el color va por-elemento y la fuente no).
  const customFont = typographyColors.font_file_url ? (
    <style>{`
      @font-face {
        font-family: '${CUSTOM_FONT_FAMILY}';
        src: url('${typographyColors.font_file_url}') format('${fontFormatFor(typographyColors.font_file_url)}');
        font-display: swap;
      }
    `}</style>
  ) : null;
  const bodyFontFamily = typographyColors.font_file_url
    ? `'${CUSTOM_FONT_FAMILY}', sans-serif`
    : undefined;

  // El acento de Car Builder (si el tenant lo configuró) PISA el primary_color
  // de la empresa -- es específicamente lo que este editor controla para ESTA
  // landing, a diferencia del branding general de la empresa.
  const primary = typographyColors.accent_color || branding.primary_color || '#10b981';
  const secondary = branding.secondary_color || '#0f172a';
  const tertiary = branding.tertiary_color || '#64748b';

  const openForm = (mode, extras = {}) => setActiveForm({ mode, ...extras });
  const closeForm = () => setActiveForm(null);

  const navLinks = useMemo(() => {
    const out = [];
    if (toggles.show_nuevos) out.push(['Nuevos', '#nuevos']);
    if (toggles.show_seminuevos) out.push(['Seminuevos', '#seminuevos']);
    if (toggles.show_promociones && promoItems.length > 0) out.push(['Promociones', '#promociones']);
    if (toggles.show_testimonios) out.push(['Testimonios', '#testimonios']);
    out.push(['Contacto', '#contacto']);
    return out;
  }, [toggles, promoItems.length]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-950 text-white" data-testid="car-site-public-loading">
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Cargando sitio...</span>
        </div>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-950 text-white p-6" data-testid="car-site-public-not-found">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold">Sitio no encontrado</h1>
          <p className="text-sm text-zinc-400">El sitio que buscas no existe o ya no está disponible.</p>
        </div>
      </div>
    );
  }

  const mapSrc = (() => {
    if (dealer.map_lat && dealer.map_lng) {
      return `https://www.google.com/maps?q=${dealer.map_lat},${dealer.map_lng}&output=embed`;
    }
    if (dealer.address || branding.address) {
      const q = encodeURIComponent(dealer.address || branding.address);
      return `https://www.google.com/maps?q=${q}&output=embed`;
    }
    return null;
  })();

  // Enlace clickeable (nav) -- mismo criterio que mapSrc, sin `&output=embed`
  // (ese modo es solo para el <iframe> incrustado en la sección Mapa).
  const mapsLink = dealer.map_lat && dealer.map_lng
    ? `https://www.google.com/maps?q=${dealer.map_lat},${dealer.map_lng}`
    : (dealer.address || branding.address)
      ? `https://www.google.com/maps?q=${encodeURIComponent(dealer.address || branding.address)}`
      : null;

  const whatsappHref = dealer.whatsapp_number
    ? `https://wa.me/${cleanPhone(dealer.whatsapp_number)}`
    : null;

  return (
    <div
      className="min-h-screen bg-white text-zinc-900"
      style={{
        '--primary': primary,
        '--secondary': secondary,
        '--tertiary': tertiary,
        ...(bodyFontFamily ? { fontFamily: bodyFontFamily } : {}),
      }}
      data-testid="car-site-public-content"
    >
      {customFont}
      {site.page_title && <title>{site.page_title}</title>}
      {/* NAV */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <a href="#top" className="flex items-center gap-2" data-testid="car-site-nav-brand">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.name} className="h-8 w-auto" />
            ) : (
              <div className="h-8 w-8 rounded grid place-items-center text-white font-bold text-sm" style={{ backgroundColor: primary }}>
                {(branding.name || 'D')[0]}
              </div>
            )}
            <span className="font-bold text-sm sm:text-base">{branding.name || 'Concesionario'}</span>
          </a>
          <div className="hidden md:flex items-center gap-5 text-sm">
            {navLinks.map(([label, href]) => (
              <a key={href} href={href} className="hover:opacity-70" data-testid={`car-site-nav-link-${label.toLowerCase()}`}>
                {label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {mapsLink && (
              <a href={mapsLink} target="_blank" rel="noreferrer" className="text-zinc-500 hover:opacity-70" title="Ubicación" data-testid="car-site-nav-location">
                <MapPin className="w-5 h-5" />
              </a>
            )}
            {whatsappHref && (
              <a href={whatsappHref} target="_blank" rel="noreferrer" className="text-emerald-600 hover:opacity-70" title="WhatsApp" data-testid="car-site-nav-whatsapp">
                <MessageCircle className="w-5 h-5" />
              </a>
            )}
            {toggles.show_cotizar && (
              <button
                type="button"
                onClick={() => openForm('cotizar')}
                className="text-white font-semibold rounded-lg px-4 py-2 text-sm"
                style={{ backgroundColor: primary }}
                data-testid="car-site-nav-cotizar"
              >
                Cotizar
              </button>
            )}
          </div>
        </nav>
      </header>

      {/* HERO */}
      <section id="top" className="relative h-[60vh] sm:h-[70vh] overflow-hidden" data-testid="car-site-hero">
        {site.hero_images && site.hero_images.length > 0 ? (
          <>
            <Carousel
              images={site.hero_images}
              autoRotate
              className="absolute inset-0"
              testid="car-site-hero-carousel"
            />
            {(toggles.show_cotizar || toggles.show_test_drive) && (
              <div
                className={
                  site.hero_scrim_side === 'right'
                    ? 'absolute inset-y-0 right-6 w-auto max-w-xs flex flex-col justify-center px-8 py-8 rounded-xl'
                    : 'absolute inset-y-0 left-0 w-1/2 sm:w-2/5 flex flex-col justify-center px-8 sm:px-12 lg:px-16'
                }
                style={
                  site.hero_scrim_side === 'right'
                    ? {
                        backgroundColor: 'rgba(0,0,0,0.62)',
                        backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                      }
                    : {
                        backgroundImage:
                          'linear-gradient(to right, rgba(0,0,0,0.72), rgba(0,0,0,0.45) 50%, transparent 100%)',
                      }
                }
                data-testid="car-site-hero-scrim"
              >
                <p className="text-white/70 text-sm font-semibold uppercase tracking-widest mb-2">
                  {branding.name || 'Concesionario'}
                </p>
                <h2
                  className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight mb-6"
                  style={{ color: typographyColors.hero_title_color || '#ffffff' }}
                >
                  Encuentra tu próximo auto
                </h2>
                <div className="flex flex-col sm:flex-row gap-3">
                  {toggles.show_cotizar && (
                    <button
                      type="button"
                      onClick={() => openForm('cotizar')}
                      className="text-white font-semibold rounded-lg px-6 py-3"
                      style={{ backgroundColor: primary }}
                      data-testid="car-site-hero-cotizar"
                    >
                      Cotizar ahora
                    </button>
                  )}
                  {toggles.show_test_drive && (
                    <button
                      type="button"
                      onClick={() => openForm('test_drive')}
                      className="bg-white text-zinc-900 font-semibold rounded-lg px-6 py-3"
                      data-testid="car-site-hero-test-drive"
                    >
                      Prueba de Manejo
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="absolute inset-0" style={{ backgroundColor: primary }} />
            <div className="relative h-full max-w-7xl mx-auto px-4 flex flex-col items-start justify-end pb-12">
              <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl font-black leading-tight max-w-2xl">
                {branding.name || 'Tu próximo auto te espera'}
              </h1>
              <p className="text-white/90 mt-3 text-lg max-w-xl">
                Encuentra el modelo perfecto para ti. Asesoría personalizada y financiamiento a tu medida.
              </p>
              <div className="flex flex-wrap gap-3 mt-6">
                {toggles.show_cotizar && (
                  <button
                    type="button"
                    onClick={() => openForm('cotizar')}
                    className="text-white font-semibold rounded-lg px-6 py-3"
                    style={{ backgroundColor: primary }}
                    data-testid="car-site-hero-cotizar"
                  >
                    Cotizar ahora
                  </button>
                )}
                {toggles.show_test_drive && (
                  <button
                    type="button"
                    onClick={() => openForm('test_drive')}
                    className="bg-white text-zinc-900 font-semibold rounded-lg px-6 py-3"
                    data-testid="car-site-hero-test-drive"
                  >
                    Prueba de Manejo
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </section>

      {/* NUEVOS */}
      {toggles.show_nuevos && (
        <section id="nuevos" className="max-w-7xl mx-auto px-4 py-14" style={nuevosBg ? { backgroundColor: nuevosBg } : undefined} data-testid="car-site-nuevos-section">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-2">
            <div>
              <p className="text-xs uppercase tracking-widest" style={{ color: tertiary }}>Catálogo</p>
              <h2 className="text-3xl font-black" style={nuevosTextColor ? { color: nuevosTextColor } : undefined}>Modelos Disponibles</h2>
            </div>
            <p className="text-sm text-zinc-500">{nuevos.length} modelo{nuevos.length === 1 ? '' : 's'}</p>
          </div>
          {nuevos.length === 0 ? (
            <p className="text-sm text-zinc-500">Próximamente cargaremos nuevos modelos.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {nuevos.map((item) => {
                const g = item?.general || {};
                const p = item?.pricing_inventory || {};
                const price = p?.sale_price || p?.list_price;
                const tags = [g?.body_type, g?.trim].filter(Boolean);
                return (
                  <article
                    key={item.id}
                    className="group overflow-hidden border border-gray-200 bg-gray-50 shadow-sm hover:shadow-xl transition-shadow"
                    data-testid={`car-site-nuevo-card-${item.id}`}
                  >
                    <div className="relative h-64 bg-zinc-100 overflow-hidden">
                      <span
                        className="absolute top-3 left-0 z-10 text-white text-[9px] uppercase font-bold tracking-widest px-4 py-1"
                        style={{ backgroundColor: primary, clipPath: BADGE_CLIP }}
                        data-testid={`car-site-nuevo-badge-${item.id}`}
                      >
                        Disponible
                      </span>
                      <img
                        src={newModelImage(item)}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute bottom-0 right-0 bg-black/90 text-white px-4 py-2 z-10">
                        <p className="text-[8px] text-white/60 uppercase tracking-widest leading-none">Precio</p>
                        <p className="font-black text-base leading-tight">{formatPrice(price)}</p>
                        <p className="text-[8px] text-white/60 uppercase tracking-widest leading-none">MXN</p>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-bold uppercase text-xl text-gray-900 mb-3 leading-tight">
                        {[g?.model, g?.trim, g?.year].filter(Boolean).join(' ')}
                      </h3>
                      {tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {tags.map((t) => (
                            <span
                              key={t}
                              className="bg-gray-100 px-3 py-1 text-[10px] uppercase font-bold"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setDetailItem(item)}
                        className="w-full border border-zinc-900 py-3 text-xs uppercase font-black tracking-widest hover:bg-zinc-900 hover:text-white transition-all"
                        data-testid={`car-site-nuevo-detail-${item.id}`}
                      >
                        Ver más
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* SEMINUEVOS */}
      {toggles.show_seminuevos && (
        <section id="seminuevos" className="bg-zinc-50 py-14" style={seminuevosBg ? { backgroundColor: seminuevosBg } : undefined} data-testid="car-site-seminuevos-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-2">
              <div>
                <p className="text-xs uppercase tracking-widest" style={{ color: tertiary }}>Inventario</p>
                <h2 className="text-3xl font-black" style={seminuevosTextColor ? { color: seminuevosTextColor } : undefined}>Seminuevos</h2>
              </div>
              <p className="text-sm text-zinc-500">{seminuevos.length} unidad{seminuevos.length === 1 ? '' : 'es'}</p>
            </div>
            {seminuevos.length === 0 ? (
              <p className="text-sm text-zinc-500">No hay seminuevos disponibles en este momento.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {seminuevos.map((u) => {
                  const status = u?.estatus || 'Disponible';
                  const badgeBg = usedBadgeBg(status, primary);
                  const specs = [
                    { icon: '⚡', label: 'Kilometraje', value: u?.kilometraje != null ? `${Number(u.kilometraje || 0).toLocaleString('es-MX')} km` : '-' },
                    { icon: '🔧', label: 'Transmisión', value: u?.transmision || '-' },
                    { icon: '⛽', label: 'Combustible', value: u?.combustible || '-' },
                    { icon: '🚪', label: 'Puertas', value: u?.puertas ?? '-' },
                  ];
                  return (
                    <article
                      key={u.id}
                      className="group rounded-2xl overflow-hidden border border-zinc-200 bg-white shadow-sm hover:shadow-xl transition-shadow"
                      data-testid={`car-site-used-card-${u.id}`}
                    >
                      <div className="relative h-56 bg-zinc-100 overflow-hidden">
                        <span
                          className="absolute top-3 left-0 z-10 text-white text-[9px] uppercase font-bold tracking-widest px-4 py-1"
                          style={{ backgroundColor: badgeBg, clipPath: BADGE_CLIP }}
                          data-testid={`car-site-used-badge-${u.id}`}
                        >
                          {status}
                        </span>
                        <img
                          src={usedImage(u)}
                          alt=""
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute bottom-0 right-0 bg-black/90 text-white px-4 py-2 z-10">
                          <p className="text-[8px] text-white/60 uppercase tracking-widest leading-none">Precio</p>
                          <p className="font-black text-base leading-tight">{formatPrice(u?.precio)}</p>
                          <p className="text-[8px] text-white/60 uppercase tracking-widest leading-none">MXN</p>
                        </div>
                      </div>
                      <div className="p-4">
                        <p
                          className="text-xs uppercase tracking-widest font-bold"
                          style={{ color: primary }}
                        >
                          {[u?.marca, u?.carroceria].filter(Boolean).join(' · ') || 'Seminuevo'}
                        </p>
                        <h3 className="text-xl font-black uppercase text-zinc-900 leading-tight">
                          {u?.modelo || '-'}
                        </h3>
                        <p className="text-sm text-zinc-500 mb-3">{u?.año || '-'}</p>

                        <div className="h-px bg-zinc-100 mb-4" />

                        <div className="grid grid-cols-2 gap-3 mb-4">
                          {specs.map((s) => (
                            <div key={s.label} className="flex items-start gap-2">
                              <span className="text-base leading-none">{s.icon}</span>
                              <div className="leading-tight">
                                <p className="text-[9px] uppercase tracking-widest text-zinc-500">{s.label}</p>
                                <p className="text-[11px] font-bold text-zinc-900">{s.value}</p>
                              </div>
                            </div>
                          ))}
                        </div>

                        {(u?.color_exterior || u?.color_interior) && (
                          <div className="flex flex-wrap gap-4 mb-5">
                            {u?.color_exterior && (
                              <div className="flex items-center gap-2">
                                <span
                                  className="w-4 h-4 rounded-full border border-zinc-300 shrink-0"
                                  style={{ backgroundColor: colorSwatch(u.color_exterior) }}
                                />
                                <div className="leading-tight">
                                  <p className="text-[9px] uppercase tracking-widest text-zinc-500">Exterior</p>
                                  <p className="text-[11px] font-bold text-zinc-900">{u.color_exterior}</p>
                                </div>
                              </div>
                            )}
                            {u?.color_interior && (
                              <div className="flex items-center gap-2">
                                <span
                                  className="w-4 h-4 rounded-full border border-zinc-300 shrink-0"
                                  style={{ backgroundColor: colorSwatch(u.color_interior) }}
                                />
                                <div className="leading-tight">
                                  <p className="text-[9px] uppercase tracking-widest text-zinc-500">Interior</p>
                                  <p className="text-[11px] font-bold text-zinc-900">{u.color_interior}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {toggles.show_auto_cuenta ? (
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() => openForm('info_seminuevo', { preselectedUsedId: u.id })}
                              className="border border-zinc-900 py-2.5 text-[11px] uppercase font-black tracking-widest hover:bg-zinc-900 hover:text-white transition-all"
                              data-testid={`car-site-used-info-${u.id}`}
                            >
                              Más Información
                            </button>
                            <button
                              type="button"
                              onClick={() => openForm('auto_cuenta')}
                              className="py-2.5 text-[11px] uppercase font-black tracking-widest text-white hover:brightness-110 transition-all"
                              style={{ backgroundColor: primary }}
                              data-testid={`car-site-used-auto-cuenta-${u.id}`}
                            >
                              Auto a Cuenta
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openForm('info_seminuevo', { preselectedUsedId: u.id })}
                            className="w-full border border-zinc-900 py-2.5 text-[11px] uppercase font-black tracking-widest hover:bg-zinc-900 hover:text-white transition-all"
                            data-testid={`car-site-used-info-${u.id}`}
                          >
                            Más Información
                          </button>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* PROMOCIONES */}
      {toggles.show_promociones && promoItems.length > 0 && (
        <section id="promociones" className="py-14" style={{ backgroundColor: promoBg || secondary }} data-testid="car-site-promo-section">
          <div className="max-w-5xl mx-auto px-4 text-center text-white">
            <p className="text-xs uppercase tracking-widest opacity-80">Promociones</p>
            <h2 className="text-3xl sm:text-4xl font-black mt-2" style={promoTextColor ? { color: promoTextColor } : undefined}>{promotions.heading || 'Promociones Exclusivas'}</h2>
            {promotions.description && (
              <p className="mt-3 text-white/80 max-w-2xl mx-auto" style={promoTextColor ? { color: promoTextColor } : undefined}>{promotions.description}</p>
            )}
            <button
              type="button"
              onClick={() => setPromosOpen(true)}
              className="mt-6 text-white font-semibold rounded-lg px-6 py-3"
              style={{ backgroundColor: primary }}
              data-testid="car-site-promo-open"
            >
              {promotions.button_text || 'Ver Promociones'}
            </button>
          </div>
        </section>
      )}

      {/* AUTO A CUENTA banner pequeño */}
      {toggles.show_auto_cuenta && (
        <section className="py-10 bg-white" data-testid="car-site-auto-cuenta-section">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-black">¿Quieres dejar tu auto a cuenta?</h2>
            <p className="text-sm text-zinc-600 mt-2">Recibimos tu auto actual como parte del pago del nuevo.</p>
            <button
              type="button"
              onClick={() => openForm('auto_cuenta')}
              className="mt-4 text-white font-semibold rounded-lg px-5 py-2.5 text-sm"
              style={{ backgroundColor: primary }}
              data-testid="car-site-auto-cuenta-btn"
            >
              Solicitar valuación
            </button>
          </div>
        </section>
      )}

      {/* TESTIMONIOS */}
      {toggles.show_testimonios && (
        <section id="testimonios" className="bg-zinc-50 py-14" data-testid="car-site-testimonios-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-8">
              <p className="text-xs uppercase tracking-widest" style={{ color: tertiary }}>Clientes felices</p>
              <h2 className="text-3xl font-black">Lo que dicen de nosotros</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { name: 'María González', text: 'Excelente atención, me ayudaron con todo el proceso de financiamiento. Súper recomendados.' },
                { name: 'Jorge Ramírez', text: 'El asesor fue muy claro, la prueba de manejo me convenció. Salí con mi auto al día siguiente.' },
                { name: 'Andrea Pérez', text: 'Me dieron muy buen precio por mi auto anterior y aprovecharon los bonos. Volveré sin duda.' },
              ].map((t) => (
                <div key={t.name} className="bg-white rounded-2xl border p-5">
                  <p className="text-sm text-zinc-700">"{t.text}"</p>
                  <p className="mt-3 text-sm font-semibold">— {t.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CONTACTO */}
      <section id="contacto" className="max-w-7xl mx-auto px-4 py-14" data-testid="car-site-contacto-section">
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <div>
            <p className="text-xs uppercase tracking-widest" style={{ color: tertiary }}>Estamos aquí</p>
            <h2 className="text-3xl font-black mt-1">Solicita Información</h2>
            <p className="mt-3 text-zinc-600">
              Déjanos tus datos y un asesor te contactará para resolver tus dudas y armar la mejor oferta.
            </p>
            <ul className="mt-5 space-y-2 text-sm">
              {(dealer.phone || branding.contact_phone) && (
                <li className="flex items-center gap-2"><Phone className="w-4 h-4" style={{ color: primary }} />{dealer.phone || branding.contact_phone}</li>
              )}
              {(dealer.address || branding.address) && (
                <li className="flex items-center gap-2"><MapPin className="w-4 h-4" style={{ color: primary }} />{dealer.address || branding.address}</li>
              )}
              {branding.contact_email && (
                <li className="flex items-center gap-2"><Mail className="w-4 h-4" style={{ color: primary }} />{branding.contact_email}</li>
              )}
              {dealer.hours && (
                <li className="flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5" style={{ color: primary }} /><span className="whitespace-pre-line">{dealer.hours}</span></li>
              )}
              {branding.website && (
                <li className="flex items-center gap-2"><Globe className="w-4 h-4" style={{ color: primary }} /><a href={branding.website} target="_blank" rel="noreferrer" className="underline">{branding.website}</a></li>
              )}
            </ul>
          </div>
          <div className="bg-zinc-50 rounded-2xl border p-5">
            <h3 className="font-bold mb-3">Cotizar</h3>
            {campaigns.cotizar ? (
              <button
                type="button"
                onClick={() => openForm('cotizar')}
                className="w-full text-white font-semibold rounded-lg py-2.5"
                style={{ backgroundColor: primary }}
                data-testid="car-site-contacto-cotizar"
              >
                Abrir formulario
              </button>
            ) : (
              <p className="text-sm text-zinc-500" data-testid="car-site-contacto-cotizar-disabled">Formulario no disponible.</p>
            )}
            {toggles.show_test_drive && (
              <button
                type="button"
                onClick={() => openForm('test_drive')}
                className="w-full mt-3 border-2 font-semibold rounded-lg py-2.5 disabled:opacity-50"
                style={{ borderColor: primary, color: primary }}
                disabled={!campaigns.test_drive}
                data-testid="car-site-contacto-test-drive"
              >
                Agendar prueba de manejo
              </button>
            )}
          </div>
        </div>
      </section>

      {/* MAPA */}
      {toggles.show_mapa && mapSrc && (
        <section className="bg-zinc-50 py-10" data-testid="car-site-mapa-section">
          <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-[1fr_320px] gap-5">
            <div className="rounded-2xl overflow-hidden border h-[360px]">
              <iframe
                title="ubicacion"
                src={mapSrc}
                width="100%"
                height="100%"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <div className="bg-white rounded-2xl border p-5 space-y-3">
              <h3 className="font-bold">Encuéntranos</h3>
              {(dealer.phone || branding.contact_phone) && (
                <p className="text-sm flex items-center gap-2"><Phone className="w-4 h-4" style={{ color: primary }} />{dealer.phone || branding.contact_phone}</p>
              )}
              {(dealer.address || branding.address) && (
                <p className="text-sm flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5" style={{ color: primary }} />{dealer.address || branding.address}</p>
              )}
              {dealer.hours && (
                <p className="text-sm flex items-start gap-2 whitespace-pre-line"><Clock className="w-4 h-4 mt-0.5" style={{ color: primary }} />{dealer.hours}</p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* FOOTER */}
      <footer className="text-white py-10" style={{ backgroundColor: secondary }} data-testid="car-site-footer">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-3 gap-6">
          <div>
            <div className="flex items-center gap-2">
              {branding.logo_url && <img src={branding.logo_url} alt="" className="h-7 w-auto" />}
              <span className="font-bold">{branding.name || 'Concesionario'}</span>
            </div>
            <p className="text-sm text-white/70 mt-2">
              Concesionario oficial. Distribuidor autorizado.
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-white/60">Contacto</p>
            <ul className="mt-2 space-y-1 text-sm text-white/80">
              {(dealer.phone || branding.contact_phone) && <li>{dealer.phone || branding.contact_phone}</li>}
              {branding.contact_email && <li>{branding.contact_email}</li>}
              {(dealer.address || branding.address) && <li>{dealer.address || branding.address}</li>}
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-white/60">Síguenos</p>
            <div className="flex gap-3 mt-3">
              {dealer.facebook_url && (
                <a href={dealer.facebook_url} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="car-site-social-fb">
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {dealer.instagram_url && (
                <a href={dealer.instagram_url} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="car-site-social-ig">
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {dealer.tiktok_url && (
                <a href={dealer.tiktok_url} target="_blank" rel="noreferrer" className="text-sm underline" data-testid="car-site-social-tt">
                  TikTok
                </a>
              )}
            </div>
          </div>
        </div>
        <p className="text-center text-xs text-white/50 mt-8">
          © {new Date().getFullYear()} {branding.name || 'Concesionario'}. Todos los derechos reservados.
        </p>
      </footer>

      {/* WhatsApp flotante */}
      {whatsappHref && (
        <a
          href={whatsappHref}
          target="_blank"
          rel="noreferrer"
          className="fixed bottom-5 right-5 z-50 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full p-4 shadow-2xl"
          aria-label="WhatsApp"
          data-testid="car-site-whatsapp-fab"
        >
          <MessageCircle className="w-6 h-6" />
        </a>
      )}

      {/* MODALES */}
      <Modal
        open={promosOpen}
        onClose={() => setPromosOpen(false)}
        title={promotions.heading || 'Promociones'}
        maxWidth="max-w-6xl"
        testid="car-site-promos-modal"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {promoItems.map((it, idx) => (
            <div key={it.id || idx} className="border border-gray-200 bg-white overflow-hidden" data-testid={`car-site-promo-item-${idx}`}>
              <img src={it.image_url || PLACEHOLDER_IMG} alt="" loading="lazy" className="w-full h-48 object-cover" />
              <div className="p-5">
                <h3 className="font-black uppercase text-lg text-gray-900 mb-3">{it.model_name}</h3>
                <div className="space-y-2 text-sm">
                  {[it.benefit_1, it.benefit_2, it.benefit_3].filter(Boolean).map((b, i) => (
                    <div key={i} className="pl-3 border-l-4" style={{ borderColor: primary }}>{b}</div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Modal>

      <ModelDetailModal
        item={detailItem}
        onClose={() => setDetailItem(null)}
        onCotizar={(id) => { setDetailItem(null); openForm('cotizar', { preselectedCarId: id }); }}
        onTestDrive={(id) => { setDetailItem(null); openForm('test_drive', { preselectedCarId: id }); }}
        branding={branding}
      />

      <LeadForm
        open={activeForm?.mode === 'cotizar'}
        onClose={closeForm}
        title="Solicitar Cotización"
        mode="cotizar"
        companyId={site.company_id}
        campaignId={campaigns.cotizar}
        branding={branding}
        nuevos={nuevos}
        seminuevos={seminuevos}
        preselectedCarId={activeForm?.preselectedCarId || ''}
      />
      <LeadForm
        open={activeForm?.mode === 'test_drive'}
        onClose={closeForm}
        title="Agendar Prueba de Manejo"
        mode="test_drive"
        companyId={site.company_id}
        campaignId={campaigns.test_drive}
        branding={branding}
        nuevos={nuevos}
        seminuevos={seminuevos}
        preselectedCarId={activeForm?.preselectedCarId || ''}
      />
      <LeadForm
        open={activeForm?.mode === 'info_seminuevo'}
        onClose={closeForm}
        title="Información de Seminuevo"
        mode="info_seminuevo"
        companyId={site.company_id}
        campaignId={campaigns.info_seminuevo}
        branding={branding}
        nuevos={nuevos}
        seminuevos={seminuevos}
        preselectedUsedId={activeForm?.preselectedUsedId || ''}
      />
      <LeadForm
        open={activeForm?.mode === 'auto_cuenta'}
        onClose={closeForm}
        title="Auto a Cuenta"
        mode="auto_cuenta"
        companyId={site.company_id}
        campaignId={campaigns.auto_cuenta}
        branding={branding}
        nuevos={nuevos}
        seminuevos={seminuevos}
      />
    </div>
  );
}
